import { useQueryClient } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useCreateBroadbandSuppression,
  useCreateSuppression,
  useDeviceSettings,
  useDeviceStatusSocket,
  useIntl,
  useStopBroadbandSuppression,
  useStopSuppression,
} from '../../hooks';
import { useGetAllSuppressions } from '../../hooks/useAllSuppressions.js';
import type { CreateBroadbandSuppressionRequest } from '../../hooks/useBroadbandSuppressions.js';
import { useLinkedDevices } from '../../hooks/useLinkedDevices.js';
import { useLinkedDeviceSuppress } from '../../hooks/useLinkedDeviceSuppress.js';
import type { SuppressionChannelPayload } from '../../hooks/useSuppressions.js';
import { hzToMhz } from '../../lib/frequency.js';
import {
  resolveDefaultBandwidthMhz,
  resolveDefaultMultiFrequencyBandwidthMhz,
  resolveDefaultMultiFrequencySignalType,
} from '../../lib/suppression-bandwidth.js';
import { cn } from '../../lib/utils.js';
import { type DetectionGroup, DeviceStatus, SignalType } from '../../types';
import type { UnifiedSuppressionStatus } from '../../types/unified-suppression.js';
import type { UnifiedSuppression } from '../../types/unified-suppression.js';
import { LinkedDeviceGroupsCompactTable } from '../detection-groups/LinkedDeviceGroupsCompactTable.js';
import { LinkedDevicesBar } from '../detection-groups/LinkedDevicesBar.js';
import { renderSignalTypeIcon } from '../SignalTypeIcon.js';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui';
import { SuppressionForm } from './SuppressionForm.js';

interface DeviceBroadbandSuppressionsDialogProps {
  deviceId: string;
  deviceSerialNumber: string;
  deviceStatus: DeviceStatus;
  canOperate: boolean;
  onOpenChange: (open: boolean) => void;
}

const PAGE_SIZE = 10;

const getStatusVariant = (status: UnifiedSuppressionStatus) => {
  switch (status) {
    case 'in_progress':
      return 'warning';
    case 'finished':
      return 'success';
    case 'stopped':
    case 'failed':
      return 'destructive';
    default:
      return 'secondary';
  }
};

const STOPPABLE_SUPPRESSION_STATUSES: ReadonlySet<UnifiedSuppressionStatus> =
  new Set<UnifiedSuppressionStatus>(['initiated', 'in_progress']);

const isBroadbandSuppressionStoppable = (suppression: UnifiedSuppression) =>
  suppression.kind === 'broadband' && STOPPABLE_SUPPRESSION_STATUSES.has(suppression.status);

const isRegularSuppressionStoppable = (suppression: UnifiedSuppression) =>
  suppression.kind === 'regular' &&
  STOPPABLE_SUPPRESSION_STATUSES.has(suppression.status) &&
  suppression.channelFrequency != null;

const isSuppressionStoppable = (suppression: UnifiedSuppression) =>
  isBroadbandSuppressionStoppable(suppression) || isRegularSuppressionStoppable(suppression);

const REPEATABLE_SUPPRESSION_STATUSES: ReadonlySet<UnifiedSuppressionStatus> =
  new Set<UnifiedSuppressionStatus>(['finished', 'stopped', 'failed']);

// Every terminal regular row repeats — including device auto-suppressions and rows that predate
// the `frequency_offset` / `priority` columns. Neither persisted parameter gates visibility: both
// are replayed when present and left to the backend's defaults when not. Only the two values the
// create request cannot do without (`channelFrequency`, `duration`) are required — the same two
// `buildRegularRepeatChannel` requires, so a regular row visible here always builds a payload.
const isRegularSuppressionRepeatable = (suppression: UnifiedSuppression) =>
  suppression.kind === 'regular' &&
  REPEATABLE_SUPPRESSION_STATUSES.has(suppression.status) &&
  suppression.channelFrequency != null &&
  suppression.duration != null;

const isBroadbandSuppressionRepeatable = (suppression: UnifiedSuppression) =>
  suppression.kind === 'broadband' && REPEATABLE_SUPPRESSION_STATUSES.has(suppression.status);

// A switch, not `a || b`: a third `kind` must fail to compile here exactly as it does in
// buildRepeatPayload, so the visibility gate and the payload builder can never disagree.
const isSuppressionRepeatable = (suppression: UnifiedSuppression): boolean => {
  switch (suppression.kind) {
    case 'broadband':
      return isBroadbandSuppressionRepeatable(suppression);
    case 'regular':
      return isRegularSuppressionRepeatable(suppression);
    default: {
      const _exhaustive: never = suppression.kind;
      return _exhaustive;
    }
  }
};

const HTTP_BAD_REQUEST = 400;
const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;

// The regular and broadband create hooks throw an error of this same shape, so one failure
// handler covers both dispatch branches.
type RepeatFailure = { status?: number; serverMessage?: string };

function getRepeatFailureMessageId(status: number | undefined): string {
  switch (status) {
    case HTTP_BAD_REQUEST:
      return 'broadbandSuppressionsDialog.actions.repeatFailedRejected';
    case HTTP_FORBIDDEN:
      return 'broadbandSuppressionsDialog.actions.repeatFailedForbidden';
    case HTTP_NOT_FOUND:
      return 'broadbandSuppressionsDialog.actions.repeatFailedDeviceNotFound';
    default:
      return 'broadbandSuppressionsDialog.actions.repeatFailed';
  }
}

/**
 * Rebuilds the create request from a recorded row, or `null` when the row is missing a field its
 * mode requires. Every value passes through unconverted — both sides are Hz, milliseconds and
 * seconds respectively.
 */
function buildBroadbandRepeatRequest(
  suppression: UnifiedSuppression,
): CreateBroadbandSuppressionRequest | null {
  const { duration, mode } = suppression;
  if (duration == null || mode == null) {
    return null;
  }

  // The read schema narrows `type` to the two signal literals and the whole page is parsed at the
  // query boundary, so an off-enum value fails the query rather than reaching a row.
  const base = { type: suppression.type as SignalType, duration };

  // Exhaustive over `mode` on purpose: the create DTO rejects any field its mode forbids, so a
  // fourth mode must fail to compile rather than post a body assembled by a spread.
  switch (mode) {
    case 'solid': {
      const { channelFrequencyStart, channelFrequencyStop } = suppression;
      if (channelFrequencyStart == null || channelFrequencyStop == null) {
        return null;
      }
      return { ...base, mode, channelFrequencyStart, channelFrequencyStop };
    }
    case 'hopping': {
      const { channelFrequencyStart, channelFrequencyStop, bandwidth, step, stepStandingTimeMs } =
        suppression;
      if (
        channelFrequencyStart == null ||
        channelFrequencyStop == null ||
        bandwidth == null ||
        step == null ||
        stepStandingTimeMs == null
      ) {
        return null;
      }
      return {
        ...base,
        mode,
        channelFrequencyStart,
        channelFrequencyStop,
        bandwidth,
        step,
        stepStandingTimeMs,
      };
    }
    case 'multi': {
      const { channelFrequencyList, bandwidth, stepStandingTimeMs } = suppression;
      if (
        channelFrequencyList == null ||
        channelFrequencyList.length === 0 ||
        bandwidth == null ||
        stepStandingTimeMs == null
      ) {
        return null;
      }
      return {
        ...base,
        mode,
        // Sorted on a copy — the array belongs to the query cache.
        channelFrequencyList: [...channelFrequencyList].sort((a, b) => a - b),
        bandwidth,
        stepStandingTimeMs,
      };
    }
  }
}

// Unlike the detections-table repeat, this table has no live detection group to retarget, so the
// replay uses the row's historical frequency; the backend re-ensures a group for it
// (LinkSuppressionDetectionGroupCommand → EnsureDetectionGroup), which must move a terminal group
// back to in-progress. A linked-device row replays as a plain regular suppression:
// `parentDetectionGroupId` is not persisted anywhere, so the parent link cannot be restored here.
function buildRegularRepeatChannel(
  suppression: UnifiedSuppression,
): SuppressionChannelPayload | null {
  const { channelFrequency, duration } = suppression;
  if (channelFrequency == null || duration == null) return null;

  return {
    channelFrequency,
    type: suppression.type as SignalType,
    duration,
    // Absent when the row has none — the backend resolves its own default rather than the client
    // fabricating one. `priority: 0` does not echo on the device wire (`Number(x) || DEFAULT`
    // resolves it to the fallback there), a pre-existing gap this repeat only replays, not causes.
    ...(suppression.priority != null ? { priority: suppression.priority } : {}),
    ...(suppression.bandwidth != null ? { bandwidth: suppression.bandwidth } : {}),
    ...(suppression.offset != null ? { offset: suppression.offset } : {}),
    isManual: true,
  };
}

type RepeatPayload =
  | { kind: 'broadband'; payload: CreateBroadbandSuppressionRequest }
  | { kind: 'regular'; channel: SuppressionChannelPayload };

function buildRepeatPayload(suppression: UnifiedSuppression): RepeatPayload | null {
  switch (suppression.kind) {
    case 'broadband': {
      const payload = buildBroadbandRepeatRequest(suppression);
      return payload && { kind: 'broadband', payload };
    }
    case 'regular': {
      const channel = buildRegularRepeatChannel(suppression);
      return channel && { kind: 'regular', channel };
    }
    default: {
      const _exhaustive: never = suppression.kind;
      return _exhaustive;
    }
  }
}

function SuppressionDurationInput({ value, testId }: { value: number; testId?: string }) {
  const intl = useIntl();

  return (
    <Input
      type="text"
      name="suppressionDuration"
      value={value}
      disabled={true}
      readOnly={true}
      wrapperClassName="w-28"
      className="h-9 w-20 rounded-md border border-border px-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/30"
      suffix={intl.formatMessage({ id: 'detections.table.secondsSuffix' })}
      aria-label={intl.formatMessage({ id: 'broadbandSuppressionsDialog.table.duration' })}
      data-testid={testId}
    />
  );
}

function SuppressionDurationCountdown({
  duration,
  startedAtMillis,
}: {
  duration: number;
  startedAtMillis: number;
}) {
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  useEffect(() => {
    const updateRemaining = () => {
      const elapsed = Math.floor((Date.now() - startedAtMillis) / 1000);
      setRemainingSeconds(Math.max(duration - elapsed, 0));
    };

    updateRemaining();
    const interval = setInterval(updateRemaining, 1000);

    return () => clearInterval(interval);
  }, [duration, startedAtMillis]);

  return <SuppressionDurationInput value={remainingSeconds} testId="suppression-duration-input" />;
}

/** Frequency cell — single value for regular, range for broadband. */
function FrequencyCell({
  suppression,
  formatFrequency,
}: {
  suppression: UnifiedSuppression;
  formatFrequency: (hz: number) => string;
}) {
  const intl = useIntl();

  if (suppression.kind === 'broadband') {
    if (
      suppression.mode === 'multi' &&
      suppression.channelFrequencyList &&
      suppression.channelFrequencyList.length > 0
    ) {
      const list = suppression.channelFrequencyList;
      const min = Math.min(...list);
      const max = Math.max(...list);
      return (
        <TableCell className="text-sm">
          {min === max ? formatFrequency(min) : `${formatFrequency(min)}–${formatFrequency(max)}`}
        </TableCell>
      );
    }

    const start =
      suppression.channelFrequencyStart != null
        ? formatFrequency(suppression.channelFrequencyStart)
        : '—';
    const stop =
      suppression.channelFrequencyStop != null
        ? formatFrequency(suppression.channelFrequencyStop)
        : '—';
    return (
      <TableCell className="text-sm">
        {start}–{stop}
      </TableCell>
    );
  }

  const freq =
    suppression.channelFrequency != null ? formatFrequency(suppression.channelFrequency) : '—';
  return (
    <TableCell className="text-sm">
      <div className="flex items-center gap-2">
        {renderSignalTypeIcon(
          suppression.type as SignalType,
          intl.formatMessage({
            id: `broadbandSuppressionsDialog.form.type.${suppression.type}`,
          }),
        )}
        <span>{freq}</span>
      </div>
    </TableCell>
  );
}

function BandwidthCell({
  suppression,
  formatFrequency,
}: {
  suppression: UnifiedSuppression;
  formatFrequency: (hz: number) => string;
}) {
  const bandwidth = suppression.bandwidth != null ? formatFrequency(suppression.bandwidth) : '—';

  return <TableCell className="text-sm text-foreground">{bandwidth}</TableCell>;
}

/** Details cell — broadband shows step/standing time; regular shows reason. */
function DetailsCell({
  suppression,
  formatFrequency,
}: {
  suppression: UnifiedSuppression;
  formatFrequency: (hz: number) => string;
}) {
  const intl = useIntl();

  if (suppression.kind === 'broadband') {
    return (
      <TableCell className="text-sm text-foreground">
        <div className="flex flex-col gap-0.5 text-xs">
          {suppression.step != null && (
            <span>
              {intl.formatMessage(
                { id: 'broadbandSuppressionsDialog.table.detailLabels.step' },
                { value: hzToMhz(suppression.step) },
              )}
            </span>
          )}
          {suppression.stepStandingTimeMs != null && (
            <span>
              {intl.formatMessage(
                { id: 'broadbandSuppressionsDialog.table.detailLabels.standingTime' },
                { value: suppression.stepStandingTimeMs },
              )}
            </span>
          )}
          {suppression.mode === 'multi' &&
            suppression.channelFrequencyList &&
            suppression.channelFrequencyList.length > 0 && (
              <span>
                {intl.formatMessage(
                  { id: 'broadbandSuppressionsDialog.table.detailLabels.frequencies' },
                  {
                    // Joined with a middle dot, not a comma: formatFrequency emits a
                    // locale thousands separator that is itself a comma in en, so
                    // "1,100, 1,150" would read ambiguously as four values.
                    value: [...suppression.channelFrequencyList]
                      .sort((a, b) => a - b)
                      .map(formatFrequency)
                      .join(' · '),
                  },
                )}
              </span>
            )}
        </div>
      </TableCell>
    );
  }

  return <TableCell className="text-sm text-foreground">{suppression.reason ?? '—'}</TableCell>;
}

function TimestampValue({ value, showDate = true }: { value: DateTime; showDate?: boolean }) {
  const intl = useIntl();
  const localizedValue = value.setLocale(intl.locale);
  const displayDate = localizedValue.toLocaleString(DateTime.DATE_SHORT);
  const displayTime = localizedValue.toFormat('HH:mm:ss');

  return (
    <time
      dateTime={value.toISO() ?? undefined}
      title={localizedValue.toFormat('yyyy-MM-dd HH:mm:ss')}
    >
      {showDate ? `${displayDate} ${displayTime}` : displayTime}
    </time>
  );
}

function TimestampCell({ suppression }: { suppression: UnifiedSuppression }) {
  const intl = useIntl();

  if (suppression.startedAt && suppression.finishedAt) {
    const isSameDay = suppression.startedAt.hasSame(suppression.finishedAt, 'day');

    return (
      <TableCell className="text-sm text-foreground" data-testid="suppression-timestamps">
        <div className="flex items-center gap-1 whitespace-nowrap">
          <TimestampValue value={suppression.startedAt} />
          <span className="text-muted-foreground">→</span>
          <TimestampValue value={suppression.finishedAt} showDate={!isSameDay} />
        </div>
      </TableCell>
    );
  }

  const labelId = suppression.finishedAt
    ? 'broadbandSuppressionsDialog.table.timestampLabels.finished'
    : suppression.startedAt
      ? 'broadbandSuppressionsDialog.table.timestampLabels.started'
      : 'broadbandSuppressionsDialog.table.timestampLabels.created';
  const value = suppression.finishedAt ?? suppression.startedAt ?? suppression.createdAt;

  return (
    <TableCell className="text-sm text-foreground" data-testid="suppression-timestamps">
      <div className="flex items-center gap-1 whitespace-nowrap">
        <span className="text-xs text-muted-foreground">
          {intl.formatMessage({ id: labelId })}:
        </span>
        <TimestampValue value={value} />
      </div>
    </TableCell>
  );
}

function DurationCell({ suppression }: { suppression: UnifiedSuppression }) {
  const startedAt = suppression.startedAt;
  const duration = suppression.duration;

  if (
    suppression.status === 'in_progress' &&
    startedAt != null &&
    duration != null &&
    duration > 0
  ) {
    return (
      <TableCell className="align-top">
        <SuppressionDurationCountdown duration={duration} startedAtMillis={startedAt.toMillis()} />
      </TableCell>
    );
  }

  if (duration != null) {
    return (
      <TableCell className="align-top">
        <SuppressionDurationInput value={duration} testId="suppression-duration-input" />
      </TableCell>
    );
  }

  return <TableCell className="text-sm">—</TableCell>;
}

function ActionsCell({
  suppression,
  canManageSuppressions,
  isDeviceOffline,
  isStopPending,
  isRepeatPending,
  onStop,
  onRepeat,
}: {
  suppression: UnifiedSuppression;
  canManageSuppressions: boolean;
  // Hides Stop, but only disables Repeat — a rejected repeat is a legitimate outcome the operator
  // is meant to see, so the click is not pre-empted by hiding the control.
  isDeviceOffline: boolean;
  isStopPending: boolean;
  isRepeatPending: boolean;
  onStop: (suppression: UnifiedSuppression) => void;
  onRepeat: (suppression: UnifiedSuppression) => void;
}) {
  const intl = useIntl();

  const showStop = canManageSuppressions && !isDeviceOffline && isSuppressionStoppable(suppression);
  const showRepeat = canManageSuppressions && isSuppressionRepeatable(suppression);

  if (!showStop && !showRepeat) {
    return <TableCell className="align-top text-sm text-muted-foreground">—</TableCell>;
  }

  const isRepeatable = buildRepeatPayload(suppression) != null;
  // `aria-label` overrides `title` as the accessible name, so one id feeds both. An in-flight
  // repeat disables the button without a reason of its own — the two states worth explaining are
  // the ones the operator cannot infer from having just clicked.
  const repeatLabelId = isDeviceOffline
    ? 'broadbandSuppressionsDialog.actions.repeatTooltipOffline'
    : isRepeatable
      ? 'broadbandSuppressionsDialog.actions.repeat'
      : 'broadbandSuppressionsDialog.actions.repeatTooltipIncomplete';

  return (
    <TableCell className="align-top">
      <div className="flex items-center gap-2">
        {showStop && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isStopPending}
            onClick={() => onStop(suppression)}
            data-testid="broadband-stop-suppression-button"
          >
            {intl.formatMessage({ id: 'broadbandSuppressionsDialog.actions.stop' })}
          </Button>
        )}
        {showRepeat && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isDeviceOffline || isRepeatPending || !isRepeatable}
            onClick={() => onRepeat(suppression)}
            title={intl.formatMessage({ id: repeatLabelId })}
            aria-label={intl.formatMessage({ id: repeatLabelId })}
            data-testid="repeat-suppression-button"
          >
            <RotateCcw className="h-4 w-4" aria-hidden />
          </Button>
        )}
      </div>
    </TableCell>
  );
}

export default function DeviceBroadbandSuppressionsDialog({
  deviceId,
  deviceSerialNumber,
  deviceStatus,
  canOperate: canManageSuppressions,
  onOpenChange,
}: DeviceBroadbandSuppressionsDialogProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { data: deviceSettings } = useDeviceSettings(deviceId);
  const [page, setPage] = useState(1);
  const [linkedDevicePage, setLinkedDevicePage] = useState(1);
  const subscribedDeviceIds = useMemo(() => [deviceId], [deviceId]);

  const allSuppressionsQuery = useGetAllSuppressions({
    deviceId,
    page,
    pageSize: PAGE_SIZE,
  });
  const { mutateAsync: stopBroadbandSuppression, isPending: isStoppingBroadbandSuppression } =
    useStopBroadbandSuppression();
  const { mutateAsync: stopSuppression, isPending: isStoppingSuppression } = useStopSuppression();
  const { mutateAsync: createBroadbandSuppression } = useCreateBroadbandSuppression();
  const { mutateAsync: createSuppression } = useCreateSuppression();
  const [pendingStopSuppressionId, setPendingStopSuppressionId] = useState<string | null>(null);
  // Per-row, not a single id: one row's in-flight repeat must not re-enable another's button, and
  // whichever request settles first must not release the lock for both.
  const [pendingRepeatSuppressionIds, setPendingRepeatSuppressionIds] = useState<Set<string>>(
    new Set(),
  );
  const pendingRepeatSuppressionIdsRef = useRef<Set<string>>(new Set());
  const [repeatError, setRepeatError] = useState<string | null>(null);
  const minAllowedMhz = useMemo(() => {
    if (!deviceSettings?.minAllowedFrequency) return null;
    const value = hzToMhz(deviceSettings.minAllowedFrequency);
    return value > 0 ? value : null;
  }, [deviceSettings?.minAllowedFrequency]);
  const maxAllowedMhz = useMemo(() => {
    if (!deviceSettings?.maxAllowedFrequency) return null;
    const value = hzToMhz(deviceSettings.maxAllowedFrequency);
    return value > 0 ? value : null;
  }, [deviceSettings?.maxAllowedFrequency]);
  const defaultSuppressionTime = deviceSettings?.suppressionTime ?? null;
  const maxSuppressionTime = deviceSettings?.maxSuppressionTime ?? null;

  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat(intl.locale, {
        maximumFractionDigits: 3,
      }),
    [intl.locale],
  );

  const suppressions = useMemo(
    () => allSuppressionsQuery.data?.suppressions ?? [],
    [allSuppressionsQuery.data?.suppressions],
  );
  const total = allSuppressionsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const isDeviceOffline = deviceStatus !== DeviceStatus.ONLINE;
  const canUseForm =
    canManageSuppressions &&
    !isDeviceOffline &&
    maxSuppressionTime !== null &&
    deviceSettings != null;

  useEffect(() => {
    if (!allSuppressionsQuery.data) {
      return;
    }

    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [allSuppressionsQuery.data, page, totalPages]);

  const invalidateAll = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['all-suppressions'] });
  }, [queryClient]);

  const canSuppressFromLinked =
    canManageSuppressions && !isDeviceOffline && Boolean(deviceSettings);

  const {
    message: linkedDeviceMessage,
    clearMessage,
    suppressFromLinkedDevice,
  } = useLinkedDeviceSuppress({
    deviceId,
    onSuccess: () => {
      setPage(1);
      invalidateAll();
    },
  });

  const handleActiveDeviceCleared = useCallback(() => {
    clearMessage();
    setLinkedDevicePage(1);
  }, [clearMessage]);

  const { linkedDevices, activeLinkedDeviceId, activeLinkedDevice, toggleLinkedDevice } =
    useLinkedDevices(deviceId, { onActiveDeviceCleared: handleActiveDeviceCleared });

  useEffect(() => {
    setLinkedDevicePage(1);
  }, [deviceId]);

  const handleToggleLinkedDevice = useCallback(
    (linkedDeviceId: string) => {
      clearMessage();
      setLinkedDevicePage(1);
      toggleLinkedDevice(linkedDeviceId);
    },
    [clearMessage, toggleLinkedDevice],
  );

  const handleLinkedDeviceSuppressGroup = useCallback(
    (group: DetectionGroup, durationSec: number, priority?: number) => {
      if (!activeLinkedDevice) return;
      return suppressFromLinkedDevice(group, activeLinkedDevice.serial, durationSec, priority);
    },
    [activeLinkedDevice, suppressFromLinkedDevice],
  );

  const handleNewBroadbandSuppression = useCallback(
    (event: { deviceId: string }) => {
      if (event.deviceId !== deviceId) return;
      setPage(1);
      invalidateAll();
    },
    [deviceId, invalidateAll],
  );

  const handleNewSuppression = useCallback(
    (event: { deviceId: string }) => {
      if (event.deviceId !== deviceId) return;
      setPage(1);
      invalidateAll();
    },
    [deviceId, invalidateAll],
  );

  useDeviceStatusSocket(
    {
      onNewBroadbandSuppression: handleNewBroadbandSuppression,
      onNewSuppression: handleNewSuppression,
    },
    subscribedDeviceIds,
  );

  const handleSuppressionCreated = useCallback(() => {
    setPage(1);
    invalidateAll();
  }, [invalidateAll]);

  const handleStopSuppression = useCallback(
    async (suppression: UnifiedSuppression) => {
      if (!isSuppressionStoppable(suppression)) return;

      setPendingStopSuppressionId(suppression.id);
      try {
        if (isBroadbandSuppressionStoppable(suppression)) {
          await stopBroadbandSuppression({
            deviceId,
            suppressionId: suppression.id,
          });
        } else {
          const channelFrequency = suppression.channelFrequency;
          if (channelFrequency == null) return;

          await stopSuppression({
            deviceId,
            payload: {
              channels: [
                {
                  id: suppression.id,
                  channelFrequency,
                  type: suppression.type as SignalType,
                },
              ],
            },
          });
        }
        invalidateAll();
      } catch {
        // Error reporting lives in the mutation hook. A benign stop is deliberately not reported
        // anywhere, so it's swallowed here on purpose — no toast, no Sentry event.
      } finally {
        setPendingStopSuppressionId(null);
      }
    },
    [deviceId, invalidateAll, stopBroadbandSuppression, stopSuppression],
  );

  const handleRepeatSuppression = useCallback(
    async (suppression: UnifiedSuppression) => {
      // React state is async, so two clicks in the same tick would both clear a state-based guard.
      // The ref is checked and claimed before the first await.
      if (pendingRepeatSuppressionIdsRef.current.has(suppression.id)) return;

      const repeatPayload = buildRepeatPayload(suppression);
      if (!repeatPayload) {
        // The button is disabled for an incomplete row, so this is unreachable from the UI — but a
        // click that does nothing at all is indistinguishable from a hung request.
        setRepeatError(
          intl.formatMessage({ id: 'broadbandSuppressionsDialog.actions.repeatFailed' }),
        );
        return;
      }

      pendingRepeatSuppressionIdsRef.current.add(suppression.id);
      setPendingRepeatSuppressionIds(new Set(pendingRepeatSuppressionIdsRef.current));
      setRepeatError(null);

      try {
        switch (repeatPayload.kind) {
          case 'broadband':
            await createBroadbandSuppression({ deviceId, payload: repeatPayload.payload });
            break;
          case 'regular':
            await createSuppression({ deviceId, payload: { channels: [repeatPayload.channel] } });
            break;
        }
        setPage(1);
        // Neither create hook is relied on to refresh the unified key this table reads — a
        // broadband success only invalidates its own list, and a regular success invalidates the
        // per-device `suppressions` key only when the response body carries a `deviceId`, which
        // this handler should not depend on. This is the only thing refreshing it on the HTTP path.
        invalidateAll();
      } catch (error) {
        const failure = error as RepeatFailure | null;
        const frame = intl.formatMessage({ id: getRepeatFailureMessageId(failure?.status) });
        setRepeatError(failure?.serverMessage ? `${frame}: ${failure.serverMessage}` : frame);
      } finally {
        pendingRepeatSuppressionIdsRef.current.delete(suppression.id);
        setPendingRepeatSuppressionIds(new Set(pendingRepeatSuppressionIdsRef.current));
      }
    },
    [createBroadbandSuppression, createSuppression, deviceId, intl, invalidateAll],
  );

  const formatFrequency = (value: number) => numberFormatter.format(hzToMhz(value));

  return (
    <Dialog open={true} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-8xl w-[95vw] h-[85vh] flex flex-col p-6 overflow-hidden"
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          if (!(event.target instanceof HTMLInputElement)) {
            onOpenChange(false);
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {intl.formatMessage(
              { id: 'broadbandSuppressionsDialog.title' },
              { serial: deviceSerialNumber },
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden flex flex-col gap-4">
          <div className="shrink-0">
            <LinkedDevicesBar
              linkedDevices={linkedDevices}
              activeLinkedDeviceId={activeLinkedDeviceId}
              onToggle={handleToggleLinkedDevice}
            />
          </div>

          <div className="flex items-start justify-end gap-4 shrink-0">
            <div className="flex items-center gap-2">
              {isDeviceOffline && (
                <Badge variant="warning-light">
                  {intl.formatMessage({ id: 'broadbandSuppressionsDialog.form.deviceOffline' })}
                </Badge>
              )}
            </div>
          </div>

          {(repeatError ?? linkedDeviceMessage) && (
            // `key` remounts on the info <-> error switch: mutating role/aria-live on a node an
            // assistive tech has already latched as a polite status region does not reliably
            // re-announce, while a fresh node inserted as role="alert" does.
            <div
              key={repeatError ? 'repeat-error' : 'linked-info'}
              role={repeatError ? 'alert' : 'status'}
              aria-live={repeatError ? 'assertive' : 'polite'}
              className={cn(
                'mb-3 rounded-md border px-3 py-2 text-xs shrink-0',
                repeatError
                  ? 'border-destructive bg-destructive-light text-destructive-dark'
                  : 'border-border bg-background-light text-foreground',
              )}
            >
              {repeatError ?? linkedDeviceMessage}
            </div>
          )}

          {activeLinkedDevice && (
            <div className="shrink-0">
              <LinkedDeviceGroupsCompactTable
                canManageSuppressions={canSuppressFromLinked}
                defaultDurationSec={deviceSettings?.suppressionTime ?? 0}
                deviceId={activeLinkedDevice.id}
                deviceSerialNumber={activeLinkedDevice.serial}
                maxSuppressionTime={deviceSettings?.maxSuppressionTime ?? 0}
                page={linkedDevicePage}
                pageSize={5}
                onPageChange={setLinkedDevicePage}
                onSuppressGroup={handleLinkedDeviceSuppressGroup}
              />
            </div>
          )}

          {canManageSuppressions && deviceSettings && (
            <SuppressionForm
              deviceId={deviceId}
              defaultSuppressionTime={defaultSuppressionTime}
              disabled={!canUseForm}
              minAllowedMhz={minAllowedMhz}
              maxAllowedMhz={maxAllowedMhz}
              maxSuppressionTime={maxSuppressionTime}
              defaultAnalogBandwidthMhz={resolveDefaultBandwidthMhz(
                SignalType.ANALOG,
                deviceSettings,
              )}
              defaultDigitalBandwidthMhz={resolveDefaultBandwidthMhz(
                SignalType.DIGITAL,
                deviceSettings,
              )}
              defaultAnalogMultiFrequencyBandwidthMhz={resolveDefaultMultiFrequencyBandwidthMhz(
                SignalType.ANALOG,
                deviceSettings,
              )}
              defaultDigitalMultiFrequencyBandwidthMhz={resolveDefaultMultiFrequencyBandwidthMhz(
                SignalType.DIGITAL,
                deviceSettings,
              )}
              defaultMultiFrequencySignalType={resolveDefaultMultiFrequencySignalType(
                deviceSettings,
              )}
              defaultBroadbandMode={deviceSettings.defaultBroadbandSuppressionMode}
              onSuppressionCreated={handleSuppressionCreated}
            />
          )}

          <div className="flex-1 min-h-0 rounded-lg border border-border bg-background-light overflow-hidden">
            <div className="overflow-auto h-full">
              <Table className="min-w-[1080px]">
                <TableHeader className="bg-background-light">
                  <TableRow>
                    <TableHead>
                      {intl.formatMessage({ id: 'broadbandSuppressionsDialog.table.status' })}
                    </TableHead>
                    <TableHead>
                      {intl.formatMessage({ id: 'broadbandSuppressionsDialog.table.frequency' })}
                    </TableHead>
                    <TableHead>
                      {intl.formatMessage({ id: 'broadbandSuppressionsDialog.table.bandwidth' })}
                    </TableHead>
                    <TableHead>
                      {intl.formatMessage({
                        id: 'broadbandSuppressionsDialog.table.detailsHeader',
                      })}
                    </TableHead>
                    <TableHead>
                      {intl.formatMessage({ id: 'broadbandSuppressionsDialog.table.duration' })}
                    </TableHead>
                    <TableHead>
                      {intl.formatMessage({ id: 'broadbandSuppressionsDialog.table.timestamps' })}
                    </TableHead>
                    <TableHead>
                      {intl.formatMessage({ id: 'broadbandSuppressionsDialog.table.actions' })}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allSuppressionsQuery.isLoading && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-6 text-foreground">
                        {intl.formatMessage({ id: 'broadbandSuppressionsDialog.table.loading' })}
                      </TableCell>
                    </TableRow>
                  )}

                  {allSuppressionsQuery.error && !allSuppressionsQuery.isLoading && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-destructive py-4">
                        <div className="flex items-center gap-2">
                          <span>
                            {intl.formatMessage({
                              id: 'broadbandSuppressionsDialog.table.failedToLoad',
                            })}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => allSuppressionsQuery.refetch()}
                          >
                            {intl.formatMessage({ id: 'common.retry' })}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}

                  {!allSuppressionsQuery.isLoading &&
                    !allSuppressionsQuery.error &&
                    suppressions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-6 text-foreground">
                          {intl.formatMessage({
                            id: 'broadbandSuppressionsDialog.table.empty',
                          })}
                        </TableCell>
                      </TableRow>
                    )}

                  {suppressions.map((suppression) => (
                    <TableRow key={suppression.id} data-testid="broadband-suppression-row">
                      {/* Status */}
                      <TableCell className="align-top">
                        <Badge
                          variant={getStatusVariant(suppression.status)}
                          className="w-fit"
                          data-testid="broadband-status-badge"
                        >
                          {intl.formatMessage({
                            id: `broadbandSuppressionsDialog.status.${suppression.status}`,
                          })}
                        </Badge>
                      </TableCell>

                      {/* Frequency */}
                      <FrequencyCell suppression={suppression} formatFrequency={formatFrequency} />

                      {/* Bandwidth */}
                      <BandwidthCell suppression={suppression} formatFrequency={formatFrequency} />

                      {/* Kind-specific details */}
                      <DetailsCell suppression={suppression} formatFrequency={formatFrequency} />

                      {/* Duration */}
                      <DurationCell suppression={suppression} />

                      {/* Progressive timestamps */}
                      <TimestampCell suppression={suppression} />

                      {/* Actions */}
                      <ActionsCell
                        suppression={suppression}
                        canManageSuppressions={canManageSuppressions}
                        isDeviceOffline={isDeviceOffline}
                        isStopPending={
                          pendingStopSuppressionId === suppression.id &&
                          (suppression.kind === 'broadband'
                            ? isStoppingBroadbandSuppression
                            : isStoppingSuppression)
                        }
                        isRepeatPending={pendingRepeatSuppressionIds.has(suppression.id)}
                        onStop={(stoppableSuppression) => {
                          void handleStopSuppression(stoppableSuppression);
                        }}
                        onRepeat={(repeatableSuppression) => {
                          void handleRepeatSuppression(repeatableSuppression);
                        }}
                      />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {!allSuppressionsQuery.isLoading && totalPages > 1 && (
            <div className="flex items-center justify-between text-xs text-foreground">
              <span>
                {intl.formatMessage(
                  { id: 'broadbandSuppressionsDialog.table.pagination' },
                  {
                    start: suppressions.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1,
                    end: suppressions.length === 0 ? 0 : Math.min(page * PAGE_SIZE, total),
                    total,
                  },
                )}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
                >
                  {intl.formatMessage({ id: 'common.previous' })}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
                >
                  {intl.formatMessage({ id: 'common.next' })}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
